import { wccModules } from 'enums/wccModules';
import { AttachmentsHelpers } from 'helpers/attachments';
import { FilesHelpers } from 'helpers/files';
import { inject, injectable } from 'inversify';
import { Subscribable, SubscribableOrNullableValue } from 'knockout';
import { NodeTracker } from 'managers/nodes/tracker';
import videoOverlayManager from 'managers/videoOverlay';
import { EffectsContainer } from 'mixins/withEffects';
import WCCVideo from 'models/attachments/video';
import './video.html';
import './video.scss';

components.preload('wcc-fluid-icon', 'wcc-image');

const videoFileTypes = [
    enums.UserFileTypes.OriginalVideo.value,
    enums.UserFileTypes.LQVideo.value,
    enums.UserFileTypes.HQVideo.value
]

interface WCCVideoConfig {
    video: SubscribableOrNullableValue<WCCVideo>
    cover: SubscribableOrNullableValue<boolean | number>
}

@injectable()
export default class WCCVideoVM {
    private video: Subscribable<WCCVideo | undefined>

    labels = labels;
    thumbnailIcon = 'fal fa-film';
    iconScale = 0.15;

    isThumbnailLoaded = ko.observable(false);

    thumbnailURL: Subscribable<string | undefined>
    icon: Subscribable<string>
    cover: SubscribableOrNullableValue<boolean | number>

    hasThumbnail: Subscribable<boolean>
    hasContent: Subscribable<boolean>
    isCompact: Subscribable<boolean>
    isTiny: Subscribable<boolean>
    
    constructor(
        @inject(wccModules.componentConfig) videoOrConfig: WCCVideo | WCCVideoConfig,
        @inject(wccModules.componentInfo) info: ko.components.ComponentInfo,
        @inject(wccModules.effects) private effects: EffectsContainer
    ) {
        let video: SubscribableOrNullableValue<WCCVideo>
        let cover: SubscribableOrNullableValue<boolean | number>

        if (videoOrConfig instanceof WCCVideo) {
            video = videoOrConfig;
        } else {
            video = videoOrConfig.video;
            cover = videoOrConfig.cover;
        }

        const { width, height } = new NodeTracker({ node: <HTMLElement>info.element });
        this.isCompact = ko.pureComputed(() => width() < 150 || height() < 150);
        this.isTiny = ko.pureComputed(() => width() < 100 || height() < 100);

        this.video = ko.flattenComputed(video);
        this.cover = cover;

        const thumbnail = this.video.pluck(v => v.thumbnail);
        this.thumbnailURL = thumbnail.pluck(th => th.link);
        this.icon = ko.pureComputed<string>(() => this.hasContent() ? 'fal fa-play' : 'far fa-clock');

        this.hasThumbnail = this.thumbnailURL.isNotNull();
        this.hasContent = this.video.pluck(video => video.hqFile() != undefined, false);

        const ratio = ko.pureComputed(() => AttachmentsHelpers.getRatio(0, 0, ko.unwrap(cover)));

        ko.applyBindingsToNode(info.element, {
            'click': () => this.showVideo(),

            'css': {
                'wcc-video': true,
                'wcc-video--with-content': this.hasContent,
                'wcc-video--compact': this.isCompact,
                'wcc-video--tiny': this.isTiny,
                'wcc-video--pending': this.hasThumbnail.not()
            },

            'style': {
                'padding-top': ko.pureComputed(() => this.isThumbnailLoaded() ? undefined : system.toPercents(ratio()))
            }
        });
    }

    private showVideo() {
        if (this.hasContent()) {
            this.video.invokeNotNull(video => {
                this.effects.register(async () => {
                    const stage = await videoOverlayManager.show(video);

                    return () => stage.close();
                });
            });
        }
    }

    private async generateThumbnailLink(video: WCCVideo) {
        const videoFiles = video.files().filter(file => videoFileTypes.includes(<number>file.type()));

        if (videoFiles.length > 0) {
            const videoFile = videoFiles[0];
            const preview = await FilesHelpers.getVideoPreview({
                blob: await FilesHelpers.loadAsBlob(<string>videoFile.link()),
                name: <string>videoFile.name()
            });

            if (preview != undefined)
                return preview.src;            
        }
    }
}